import { useEffect, useState } from "react";
import { api, apiError, fmtDate } from "../lib/api";
import { UserPlus, KeyRound, Power } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
    { key: "SITE_MANAGER", label: "Site Manager" },
    { key: "PROJECT_MANAGER", label: "Project Manager" },
    { key: "HO_ADMIN", label: "HO Admin" },
];

const LANGS = [
    { key: "en", label: "English" },
    { key: "hi", label: "Hindi" },
    { key: "bn", label: "Bengali" },
];

export default function UsersAdminPage() {
    const [users, setUsers] = useState([]);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawer, setDrawer] = useState(null); // null | "new" | user obj (edit)
    const [resetFor, setResetFor] = useState(null); // user obj

    async function load() {
        setLoading(true);
        try {
            const [u, s] = await Promise.all([api.get("/users"), api.get("/sites")]);
            setUsers(u.data);
            setSites(s.data);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    async function toggleActive(u) {
        try {
            await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
            toast.success(`${u.name} ${!u.is_active ? "activated" : "deactivated"}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">User Management</h1>
                </div>
                <button onClick={() => setDrawer("new")} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="new-user-btn">
                    <UserPlus className="w-4 h-4" /> Create user
                </button>
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead className="bg-[#F4F6F9]">
                            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Role</th>
                                <th className="px-4 py-3">Sites</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan="6" className="p-6 text-center text-sm text-gray-500">Loading…</td></tr>
                            ) : users.map((u) => (
                                <tr key={u.id} className="hover:bg-[#FAFBFC]" data-testid={`user-row-${u.email}`}>
                                    <td className="px-4 py-3 font-medium text-[#1E3355]">{u.name}</td>
                                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                                    <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F4F6F9] text-[#1E3355] border border-gray-200">{u.role.replace("_", " ")}</span></td>
                                    <td className="px-4 py-3 text-xs text-gray-600">
                                        {u.site_ids?.length ? u.site_ids.map((sid) => sites.find((x) => x.id === sid)?.name).filter(Boolean).join(", ") : <span className="text-gray-400">All / none</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        {u.is_active ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                                        ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500 border border-gray-200">Disabled</span>
                                        )}
                                        {u.must_change_password && (
                                            <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">Must reset</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => setDrawer(u)} className="px-2 py-1 text-xs font-medium text-[#1E3355] hover:bg-gray-100 rounded" data-testid={`edit-user-${u.email}`}>Edit</button>
                                            <button onClick={() => setResetFor(u)} className="p-1.5 rounded hover:bg-gray-100 text-gray-600" title="Force password reset" data-testid={`force-reset-${u.email}`}>
                                                <KeyRound className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => toggleActive(u)} className={`p-1.5 rounded hover:bg-gray-100 ${u.is_active ? "text-[#E02B2B]" : "text-emerald-600"}`} title={u.is_active ? "Deactivate" : "Activate"} data-testid={`toggle-active-${u.email}`}>
                                                <Power className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {drawer && (
                <UserDrawer
                    key={drawer === "new" ? "new" : drawer.id}
                    user={drawer === "new" ? null : drawer}
                    sites={sites}
                    onClose={() => setDrawer(null)}
                    onSaved={() => { setDrawer(null); load(); }}
                />
            )}
            {resetFor && (
                <ForceResetModal user={resetFor} onClose={() => setResetFor(null)} onDone={() => { setResetFor(null); load(); }} />
            )}
        </div>
    );
}

function UserDrawer({ user, sites, onClose, onSaved }) {
    const isNew = !user;
    const [form, setForm] = useState({
        name: user?.name || "",
        email: user?.email || "",
        password: "",
        role: user?.role || "SITE_MANAGER",
        site_ids: user?.site_ids || [],
        language: user?.language || "en",
        is_active: user?.is_active ?? true,
    });
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            if (isNew) {
                await api.post("/users", form);
                toast.success(`Created ${form.email}`);
            } else {
                const payload = { name: form.name, role: form.role, site_ids: form.site_ids, language: form.language, is_active: form.is_active };
                await api.patch(`/users/${user.id}`, payload);
                toast.success("User updated");
            }
            onSaved();
        } catch (e) {
            toast.error(apiError(e));
        } finally { setSaving(false); }
    }

    function toggleSite(id) {
        setForm((f) => ({ ...f, site_ids: f.site_ids.includes(id) ? f.site_ids.filter((x) => x !== id) : [...f.site_ids, id] }));
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" data-testid="user-drawer">
            <form onSubmit={submit} className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl">
                <div className="p-5 border-b border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{isNew ? "New user" : "Edit user"}</div>
                    <div className="font-display text-lg font-bold text-[#1E3355]">{form.name || "—"}</div>
                </div>
                <div className="p-5 space-y-4">
                    <Field label="Name">
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={fieldCls} data-testid="drawer-name" />
                    </Field>
                    <Field label="Email">
                        <input type="email" value={form.email} disabled={!isNew} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={`${fieldCls} disabled:bg-gray-50`} data-testid="drawer-email" />
                    </Field>
                    {isNew && (
                        <Field label="Temporary password">
                            <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className={fieldCls} data-testid="drawer-password" />
                        </Field>
                    )}
                    <Field label="Role">
                        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={fieldCls} data-testid="drawer-role">
                            {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Site assignments">
                        <div className="flex flex-wrap gap-2">
                            {sites.map((s) => {
                                const active = form.site_ids.includes(s.id);
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => toggleSite(s.id)}
                                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-600 border-gray-200 hover:border-[#1E3355]"}`}
                                        data-testid={`drawer-site-${s.name}`}
                                    >{s.name}</button>
                                );
                            })}
                        </div>
                        {form.role !== "SITE_MANAGER" && (
                            <p className="mt-1 text-[11px] text-gray-500">Non-site-manager roles have access to all sites regardless of assignments.</p>
                        )}
                    </Field>
                    <Field label="Language preference">
                        <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className={fieldCls} data-testid="drawer-language">
                            {LANGS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                        </select>
                    </Field>
                    {!isNew && (
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="drawer-active" />
                            Active
                        </label>
                    )}
                </div>
                <div className="p-5 border-t border-gray-100 flex gap-2 sticky bottom-0 bg-white">
                    <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-md border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="drawer-cancel">Cancel</button>
                    <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold disabled:opacity-60" data-testid="drawer-save">
                        {saving ? "Saving…" : isNew ? "Create user" : "Save"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function ForceResetModal({ user, onClose, onDone }) {
    const [pw, setPw] = useState("");
    const [saving, setSaving] = useState(false);
    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/users/${user.id}/force-password-reset`, { temp_password: pw });
            toast.success(`Temp password set. ${user.email} must change on next login.`);
            onDone();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="force-reset-modal">
            <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="p-5">
                    <div className="font-display font-bold text-lg text-[#1E3355]">Force password reset</div>
                    <div className="text-sm text-gray-600 mt-1">Set a temporary password for <span className="font-medium">{user.email}</span>. They must change it on next login.</div>
                    <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} placeholder="Temporary password" className={`${fieldCls} mt-4`} data-testid="force-reset-input" />
                </div>
                <div className="flex border-t border-gray-100">
                    <button type="button" onClick={onClose} className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="force-reset-cancel">Cancel</button>
                    <button type="submit" disabled={saving} className="flex-1 py-3 text-sm font-semibold text-white bg-[#E02B2B] hover:bg-[#C92525]" data-testid="force-reset-submit">
                        {saving ? "Saving…" : "Set temp password"}
                    </button>
                </div>
            </form>
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]";
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            {children}
        </div>
    );
}
